import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ImpersonationContext from "./ImpersonationContext";
import * as apicall from "../Apicall";

// localStorage keys
const KEYS = {
  IS_IMPERSONATING: "isImpersonating",
  VENDOR_ID: "impersonatedVendorId",
  VENDOR_NAME: "impersonatedVendorName",
  ADMIN_TOKEN: "adminBackupToken",
  ADMIN_USER_ID: "adminBackupUserId",
  ADMIN_USER_ROLE: "adminBackupUserRole",
};

const ImpersonationProvider = ({ children }) => {
  const navigate = useNavigate();

  const [isImpersonating, setIsImpersonating] = useState(
    () => localStorage.getItem(KEYS.IS_IMPERSONATING) === "true"
  );
  const [impersonatedVendorId, setImpersonatedVendorId] = useState(
    () => localStorage.getItem(KEYS.VENDOR_ID)
  );
  const [impersonatedVendorName, setImpersonatedVendorName] = useState(
    () => localStorage.getItem(KEYS.VENDOR_NAME) || ""
  );

  // Sync from localStorage on mount (handles page refresh during impersonation)
  useEffect(() => {
    const stored = localStorage.getItem(KEYS.IS_IMPERSONATING) === "true";
    if (stored) {
      setIsImpersonating(true);
      setImpersonatedVendorId(localStorage.getItem(KEYS.VENDOR_ID));
      setImpersonatedVendorName(localStorage.getItem(KEYS.VENDOR_NAME) || "");
    }
  }, []);

  const impersonate = useCallback(
    async (vendorUserId, vendorName) => {
      try {
        const res = await apicall.post(
          { vendor_user_id: vendorUserId },
          "admin_impersonate"
        );

        if (!res.success || !res.token) {
          throw new Error(res.message || "Failed to start impersonation");
        }

        // Back up admin session
        localStorage.setItem(KEYS.ADMIN_TOKEN, localStorage.getItem("token"));
        localStorage.setItem(KEYS.ADMIN_USER_ID, localStorage.getItem("userId"));
        localStorage.setItem(KEYS.ADMIN_USER_ROLE, localStorage.getItem("userRole"));

        // Activate vendor session
        localStorage.setItem("token", res.token);
        localStorage.setItem("userId", String(res.vendor.user_id));
        localStorage.setItem("userRole", "3");

        // Store impersonation state
        localStorage.setItem(KEYS.IS_IMPERSONATING, "true");
        localStorage.setItem(KEYS.VENDOR_ID, String(vendorUserId));
        localStorage.setItem(KEYS.VENDOR_NAME, vendorName);

        // Notify listeners about role + user change
        window.dispatchEvent(new StorageEvent("storage", { key: "userRole", newValue: "3" }));
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "userId",
            newValue: String(res.vendor.user_id),
          })
        );

        setIsImpersonating(true);
        setImpersonatedVendorId(String(vendorUserId));
        setImpersonatedVendorName(vendorName);

        navigate("/dashboard");
      } catch (err) {
        throw err;
      }
    },
    [navigate]
  );

  const exitImpersonation = useCallback(async () => {
    const adminToken = localStorage.getItem(KEYS.ADMIN_TOKEN);
    const adminUserId = localStorage.getItem(KEYS.ADMIN_USER_ID);
    const adminUserRole = localStorage.getItem(KEYS.ADMIN_USER_ROLE);
    const vendorId = localStorage.getItem(KEYS.VENDOR_ID);

    // Restore admin session FIRST so the exit-log API call uses the admin token
    if (adminToken) localStorage.setItem("token", adminToken);
    if (adminUserId) localStorage.setItem("userId", adminUserId);
    if (adminUserRole) localStorage.setItem("userRole", adminUserRole);

    // Fire-and-forget exit log (now using restored admin token)
    try {
      if (vendorId) {
        apicall.post(
          { vendor_user_id: parseInt(vendorId) },
          "admin_exit_impersonation"
        );
      }
    } catch (_) {
      // Non-blocking
    }

    // Notify listeners about role + user change (admin role restored)
    window.dispatchEvent(new StorageEvent("storage", { key: "userRole", newValue: adminUserRole || null }));
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: "userId",
        newValue: adminUserId || null,
      })
    );

    // Clear impersonation state
    [
      KEYS.IS_IMPERSONATING,
      KEYS.VENDOR_ID,
      KEYS.VENDOR_NAME,
      KEYS.ADMIN_TOKEN,
      KEYS.ADMIN_USER_ID,
      KEYS.ADMIN_USER_ROLE,
    ].forEach((k) => localStorage.removeItem(k));

    setIsImpersonating(false);
    setImpersonatedVendorId(null);
    setImpersonatedVendorName("");

    navigate("/vendors");
  }, [navigate]);

  const value = useMemo(
    () => ({
      isImpersonating,
      impersonatedVendorId,
      impersonatedVendorName,
      impersonate,
      exitImpersonation,
    }),
    [isImpersonating, impersonatedVendorId, impersonatedVendorName, impersonate, exitImpersonation]
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationProvider;
